import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  ButtonProps,
  DialogActions,
  DialogContent,
  DialogContentProps,
  DialogProps,
  DialogTitle,
  Dialog as MUIDialog,
  Stack,
  SxProps,
  Typography,
  useTheme,
} from "@mui/material";
import IconButton from "@mui/material/IconButton";
import React from "react";
import { SaveButton, SaveButtonProps } from "./SaveButton";

export function SafeDialog(props: DialogProps) {
  return props.open ? (
    <MUIDialog {...props} open={true}>
      {props.children}
    </MUIDialog>
  ) : (
    <></>
  );
}
type Props = Omit<DialogProps, "onClose" | "content" | "title"> & {
  /** Function called when the dialog is closed. Can be a simple callback or receive the close reason. */
  onClose:
    | (() => void)
    | ((
        event: React.MouseEvent<HTMLElement>,
        reason:
          | "backdropClick"
          | "escapeKeyDown"
          | "cancelButtonClick"
          | "closeButtonClick"
      ) => void);
  /** Title displayed at the top of the dialog */
  title?: React.ReactNode;
  /** Optional subheader displayed below the title */
  subheader?: string;
  /** Optional description displayed in the content area of the dialog */
  description?: React.ReactNode;
  /** Optional element to display in the title bar to the left of the close button */
  titleBarActionElement?: React.ReactNode;

  /** Additional Primary Actions. These will display to the left of Cancel and Primary action button */
  additionalPrimaryActionElements?: React.ReactNode[];
  /** Secondary Actions will display on the bottom left of the action toolbar */
  secondaryActionElements?: React.ReactNode[];
  cancelButtonProps?: ButtonProps & { text?: string };
  /** Whether to show a primary action (e.g., Save button). Defaults to true. */
  noPrimaryAction?: boolean;
  /** Optionally provide a component to use as the primary action button. `primaryActionButtonProps` do not apply to this */
  primaryActionButton?: React.ReactNode;
  /** Props to configure the Save button (shown if `noPrimaryAction` is false) */
  primaryActionButtonProps?: SaveButtonProps;
  /** Optional styles to apply to the content area of the dialog */
  contentSx?: SxProps;
  DialogContentProps?: DialogContentProps;
};

export function Dialog(props: Props) {
  const {
    title,
    subheader,
    description,
    titleBarActionElement,
    noPrimaryAction,
    primaryActionButtonProps,
    primaryActionButton,
    additionalPrimaryActionElements,
    secondaryActionElements,
    onClose,
    children,
    cancelButtonProps,
    contentSx,
    DialogContentProps,
    ...dialogProps
  } = props;
  const theme = useTheme();

  const noActions =
    !secondaryActionElements?.length &&
    !additionalPrimaryActionElements?.length &&
    noPrimaryAction;

  return (
    <SafeDialog scroll="paper" {...dialogProps} onClose={onClose}>
      <DialogTitle
        justifyContent={title ? "space-between" : "flex-end"}
        display="flex"
        alignItems="center"
      >
        {title}
        <Box display="flex" alignItems="center" gap={1}>
          {titleBarActionElement}
          <IconButton
            aria-label="close"
            onClick={(e) => onClose(e, "closeButtonClick")}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent {...DialogContentProps}>
        <Stack spacing={2} pt={theme.spacing(1.5)} sx={contentSx}>
          {subheader && (
            <Typography fontWeight={500} variant="subtitle1">
              {subheader}
            </Typography>
          )}
          {description && (
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              {description}
            </Typography>
          )}
          {children}
        </Stack>
      </DialogContent>

      {!noActions && (
        <DialogActions>
          <Stack
            direction="row"
            justifyContent="space-between"
            gap={theme.spacing(2)}
            flex={1}
          >
            {...secondaryActionElements ?? []}
            <Box
              sx={{
                display: "flex",
                gap: theme.spacing(2),
                justifyContent: "flex-end",
                alignItems: "center",
                flex: 1,
              }}
            >
              {...additionalPrimaryActionElements ?? []}
              {!noPrimaryAction && (
                <Button
                  onClick={(e) => onClose(e, "cancelButtonClick")}
                  disabled={primaryActionButtonProps?.loading}
                  {...cancelButtonProps}
                >
                  {cancelButtonProps?.text ?? "Cancel"}
                </Button>
              )}
              {!noPrimaryAction &&
                (primaryActionButton ?? (
                  <SaveButton {...primaryActionButtonProps} />
                ))}
            </Box>
          </Stack>
        </DialogActions>
      )}
    </SafeDialog>
  );
}
